import type { UniversalLinkType } from '@bentley/pw-link-ui';
import { datasourcePrefix } from '@bentley/pw-wac';

type UniversalLinkParams = {
  datasource: string | null;
  objectId: string | null;
  objectType: 'doc' | 'folder' | null;
  savedSearchId: string | null;
  workAreaId: string | null;
  projectId: string | null;
  app: UniversalLinkType | null;
};

const datasourceParams = ['datasource', 'ds'];
const objectIdParams = ['objectId', 'id', 'doc', 'fld'];
const objectTypeParams = ['objectType', 't'];
const savedSearchIdParams = ['savedSearchId', 'sid'];
const workAreaIdParams = ['workAreaId', 'wa'];
const projectIdParams = ['projectId', 'p'];

// Todo: rework this to avoid errors if a consumer only passes in one type of param (e.g., does not use new type)
export function readSearchParam<T extends string>(params: string[]): T | null {
  const url = new URL(location.href);
  const searchParams = url.searchParams;

  let value: string | null = '';
  for (const param of params) {
    value = searchParams.get(param);
    if (value == null) {
      value = searchParams.get(param.toLowerCase());
    }

    value = processSearchParam(param, value);

    if (value) {
      return value as T;
    }

    // Determining objectType when link is shortened; need to check whether 'doc' or 'fld' is in the params
    if (objectTypeParams.includes(param)) {
      if (searchParams.has('doc')) {
        return 'doc' as T;
      }
      if (searchParams.has('fld')) {
        return 'folder' as T;
      }
    }
  }

  return null;
}

function processSearchParam(
  param: string,
  value: string | null
): string | null {
  if (value == null) {
    return value;
  }

  // Add 'Bentley.PW--' to ds name if it's not already there
  if (datasourceParams.includes(param)) {
    if (value && !value.startsWith(datasourcePrefix)) {
      value = datasourcePrefix + value;
    }
  }

  // Add back hyphens into GUIDs if they are not already there
  if (value.indexOf('-') === -1) {
    if (
      objectIdParams.includes(param) ||
      workAreaIdParams.includes(param) ||
      projectIdParams.includes(param)
    ) {
      value = value.replace(
        /(.{8})(.{4})(.{4})(.{4})(.{12})/,
        '$1-$2-$3-$4-$5'
      );
    }
  }

  return value;
}

export function readUniversalLinkParams(): UniversalLinkParams {
  const datasource = readSearchParam(datasourceParams);
  const objectId = readSearchParam(objectIdParams);
  const objectType = readSearchParam<'doc' | 'folder'>(objectTypeParams);
  const savedSearchId = readSearchParam(savedSearchIdParams);
  const workAreaId = readSearchParam(workAreaIdParams);
  const projectId = readSearchParam(projectIdParams);
  const app = readSearchParam<UniversalLinkType>(['app']);

  return {
    datasource,
    objectId,
    objectType,
    savedSearchId,
    workAreaId,
    projectId,
    app
  };
}
